import { PropsWithChildren } from 'lib-shared-types-utils';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { type SidekickActivity, type SidekickTask } from './sidekick.types';

type SidekickTasks = {
  task: SidekickTask | null;
  register: (task: SidekickTask) => () => void;
  activity: SidekickActivity;
  reportActivity: (activity: SidekickActivity) => void;
};

const SidekickTasksContext = createContext<SidekickTasks | null>(null);

export const SidekickTasksProvider = ({
  children,
}: PropsWithChildren): JSX.Element => {
  const [registered, setRegistered] = useState<readonly SidekickTask[]>([]);
  const [activity, setActivity] = useState<SidekickActivity>('closed');

  const register = useCallback((task: SidekickTask) => {
    setRegistered((prev) => [...prev, task]);
    return () =>
      setRegistered((prev) => prev.filter((entry) => entry !== task));
  }, []);

  const value = useMemo<SidekickTasks>(
    () => ({
      task: registered.at(-1) ?? null,
      register,
      activity,
      reportActivity: setActivity,
    }),
    [registered, register, activity],
  );

  return (
    <SidekickTasksContext.Provider value={value}>
      {children}
    </SidekickTasksContext.Provider>
  );
};

/** The most recently registered task, which the Sidekick works on. */
export const useSidekickTask = (): SidekickTask | null =>
  useContext(SidekickTasksContext)?.task ?? null;

export const useSidekickActivity = (): SidekickActivity =>
  useContext(SidekickTasksContext)?.activity ?? 'closed';

/** Reports what the Sidekick is doing; it reads as closed once unmounted. */
export const useReportSidekickActivity = (activity: SidekickActivity): void => {
  const reportActivity = useContext(SidekickTasksContext)?.reportActivity;

  useEffect(() => reportActivity?.(activity), [reportActivity, activity]);
  useEffect(() => () => reportActivity?.('closed'), [reportActivity]);
};

/**
 * Lets the Sidekick work with the page while mounted: the brief goes with
 * every prompt, and the tools run in the browser against page state. Pass a
 * stable reference, then `startSidekickTask` to begin and `notifySidekick` to
 * report what the user did on the page.
 *
 * @example
 *   const task = useMemo<SidekickTask>(
 *     () => ({
 *       id: 'macro-editor',
 *       brief: 'The user is writing a macro. Fill it in with setMacroText.',
 *       tools: {
 *         setMacroText: {
 *           title: 'Writing the macro',
 *           description: 'Replaces the macro text on the page.',
 *           inputSchema: {
 *             type: 'object',
 *             properties: { text: { type: 'string' } },
 *             required: ['text'],
 *           },
 *           execute: ({ text }: { text: string }) => {
 *             setText(text);
 *             return 'The macro text is filled in on the page.';
 *           },
 *         },
 *       },
 *     }),
 *     [],
 *   );
 *   useRegisterSidekickTask(task);
 */
export const useRegisterSidekickTask = (task: SidekickTask | null): void => {
  const register = useContext(SidekickTasksContext)?.register;

  useEffect(() => (task ? register?.(task) : undefined), [register, task]);
};
